import { formatToArray, generateIntent, generateScheme, generateUniversalLink, parseUrl } from './util/helper';
import { Launcher } from './util/launcher';
import { Logger } from './util/logger';
import {
  AppLauncherConfig,
  AppLauncherOpenOption,
  OpenContext,
  FnFallbackCtx,
  Scheme,
  AppLauncherProcessedOption,
  ProcessedConfig,
  StaticAppLauncherOpenOptions,
  LaunchStrategy,
  FnNext,
  Intent,
} from './types';
import * as ua from './util/ua';
import { parse, StringifyOptions } from 'query-string';

export interface IAppLauncher {
  /**
   * 尝试打开app
   *
   * @param openOptions - 打开app的参数
   * @param qsOption - 将 object 序列化为 query 使用的 stringifyOptions
   *
   * @returns 是否打开成功，不一定准确
   */
  tryOpen(openOptions?: AppLauncherOpenOption, qsOption?: StringifyOptions): Promise<boolean>;

  /**
   * 获取传入的配置参数
   */
  getConfig(): AppLauncherConfig;

  /**
   * 设置参数
   */
  setConfig(config: AppLauncherConfig): this;
}


interface LaunchUrl {
  schemeUrl: string;
  intentUrl: string;
}

/**
 * 基础的 App 拉起器，只包含基础功能
 * 如果想要在微信、手Q环境下检测、安装和拉起可使用NativeLauncher
 */
export class AppLauncher implements IAppLauncher {
  /**
   * 拉起App，静态函数
   *
   * @param openOptions - 设置
   * @param stringifyOptions - query-string 设置
   * @param isLog - 是否开启log
   */
  public static tryOpen(
    openOptions: StaticAppLauncherOpenOptions = {},
    stringifyOptions?: StringifyOptions,
    isLog?: boolean,
  ): Promise<boolean> {
    const { intent } = openOptions;
    if (intent && !intent.package) {
      throw new Error('Must specify intent.package when using intent');
    }
    return new AppLauncher({
      logger: { logInfo: isLog },
      intent,
    }).tryOpen(openOptions, stringifyOptions);
  }

  public static generateScheme(...args: Parameters<typeof generateScheme>): ReturnType<typeof generateScheme> {
    return generateScheme(...args);
  }

  public static generateIntent(...args: Parameters<typeof generateIntent>): ReturnType<typeof generateIntent> {
    return generateIntent(...args);
  }

  /**
   * TODO:
   *
   * @internal
   */
  // eslint-disable-next-line function-paren-newline
  public static generateUniversalLink(
    ...args: Parameters<typeof generateUniversalLink>): ReturnType<typeof generateUniversalLink> {
    return generateUniversalLink(...args);
  }

  private config!: AppLauncherConfig;

  private logger: Logger;

  public constructor(config?: AppLauncherConfig) {
    this.logger = new Logger(this.constructor.name);
    if (config) {
      this.setConfig(config);
    } else {
      this.setConfig({ scheme: { protocol: '' } });
    }
  }

  /**
   * 尝试打开App
   *
   */
  public async tryOpen(openOptions: AppLauncherOpenOption = {}, stringifyOptions?: StringifyOptions): Promise<boolean> {
    // 处理数据
    const qsOptions = stringifyOptions ?? this.config.stringifyOptions;
    const checkDelay = openOptions.checkDelay ?? this.config.checkDelay;
    const openOpts = openOptions;
    const { model, intent, scheme: schemeProcessed } = this.getProcessedConfig(openOptions, qsOptions);

    /**
     * 经过处理好的参数
     */
    let options: AppLauncherProcessedOption = {
      model,
      fallback: openOpts.fallback,
      params: schemeProcessed.params,
      path: schemeProcessed.path,
      anchor: schemeProcessed.anchor,
    };

    /**
     * 传入给beforeLaunch勾子
     */
    if (this.config.beforeLaunch) {
      const result = this.config.beforeLaunch(options);
      // 兼容异步函数
      options = result instanceof Promise ? await result : result;
    }

    const scheme: Scheme = {
      ...schemeProcessed,
      params: options.params,
      path: options.path,
      anchor: options.anchor,
    };

    // 计算出fallback的值
    // 判断fallback是否为function然后得到fallback的值
    const fb = options.fallback ?? this.config.fallback;
    const { fallbackUrl, isDownloadLink } = this.getFallbackUrl(fb, {
      scheme,
      intent,
      params: options.params,
      path: options.path,
      anchor: options.anchor,
      model: options.model,
    });

    // 设置intent的fallback
    if (intent) {
      intent.fallback = fallbackUrl;
    }

    this.logger.log('Launching App');
    this.logger.logKV('Launch Info', { scheme, intent });
    this.logger.logKV('FallbackUrl', fallbackUrl);

    /**
     * 用于传递给自定义拉起函数，以及默认的拉起函数
     * @param generateUrl - 是否要生成url
     */
    const getContext = (): OpenContext => {
      let intentUrl = '';
      let schemeUrl = '';
      intentUrl = intent ? generateIntent(intent, scheme, qsOptions) : '';
      schemeUrl = generateScheme(scheme, qsOptions);
      return {
        isDownloadLink,
        fallbackUrl,
        intentUrl,
        params: options.params,
        scheme,
        schemeUrl,
        intent,
        model: options.model,
        checkDelay,
        logger: this.logger,
        stringifyOptions: qsOptions,
      };
    };


    const launchStrategies = formatToArray(this.config.customLaunch);
    // 最后使用默认策略
    launchStrategies.push(this.originalLaunchStrategy.bind(this));
    const launcher = new Launcher({ checkDelay }, {
      beforeStart: (url, method) => {
        this.logger.logKV('Launch URL', url);
        this.logger.logKV('Launch Method', method);
      },
    });

    // 将队列中的自定义函数执行
    const isSuccess = await this.queueCustomLaunch(launchStrategies, getContext(), launcher);
    this.afterLog(isSuccess);
    return isSuccess;
  }


  public getConfig(): AppLauncherConfig {
    // 返回clone值
    return { ...this.config };
  }

  public setConfig(config: AppLauncherConfig): this {
    this.config = Object.assign({}, this.config, config);

    // 控制控制台输出
    if (!this.config.logger?.logInfo) {
      this.logger.mute();
    } else {
      this.logger.unmute();
    }
    return this;
  }

  /**
   * 如果传入的是一个数组，则使用队列的方式依次调用
   */
  private async queueCustomLaunch(
    launchStrategies: LaunchStrategy[],
    initialContext: OpenContext,
    launcher: Launcher,
  ): Promise<boolean> {
    if (launchStrategies.length) {
      const nextFn = (n: number, context: OpenContext) => {
        const fnNext: FnNext = async (ctx) => {
          if (n + 1 === launchStrategies.length) {
            const strategyFn = launchStrategies[n];
            // 如果最后一个策略调用了next方法，则使用 async() => false来代替
            return strategyFn(ctx ?? context, launcher, async () => false);
          }
          const strategyFn = launchStrategies[n];
          return strategyFn(ctx ?? context, launcher, nextFn(n + 1, ctx ?? context));
        };
        return fnNext;
      };

      const strategyFn = launchStrategies[0];
      return strategyFn(initialContext, launcher, nextFn(1, initialContext));
    }
    return false;
  }

  /**
   * 计算出完整路径
   */
  private getPath(basePath: string, path: string): string {
    if (path.slice(0, 2) === '//') {
      return path;
    }
    if (path[0] === '/') {
      return path;
    }
    return path ? `${basePath}/${path}` : basePath;
  }

  /**
   * 根据传入的path参数计算出scheme的各个部分
   */
  private getProcessedConfig(openOptions: AppLauncherOpenOption, qsOption?: StringifyOptions): ProcessedConfig {
    const openOpts = openOptions;

    // 解析传入的 path参数
    const parsedPath = parseUrl(openOptions.path || '');

    const parseOptions: Record<string, unknown> = {};
    // 配置parseOptions
    if (qsOption) {
      (['arrayFormat', 'arrayFormatSeparator', 'sort'] as Array<keyof StringifyOptions>).forEach((key) => {
        if (qsOption[key]) {
          parseOptions[key] = qsOption[key];
        }
      });
    }
    // 把参数组合起来
    const paramInPath = parse(parsedPath.params ?? '', { ...parseOptions, decode: true });

    if (typeof openOpts.params === 'string') {
      openOpts.params = parse(openOpts.params, { ...parseOptions, decode: true });
    }

    const params = Object.assign({}, this.config.globalParams, paramInPath, openOpts.params);
    openOpts.params = params;

    // protocol 有值，则覆盖原来的
    const protocol = parsedPath.protocol ?? this.config.scheme?.protocol;
    if (!protocol || protocol === '') {
      throw new Error('missing protocol in url');
    }

    // host 有值，覆盖原来的
    const host = parsedPath.host ?? this.config.scheme?.host;

    // port 有值，覆盖原来的
    const port = parsedPath.port ?? this.config.scheme?.port;

    // 如果解析出protocol, host, port被修改，则直接使用path
    // 如果没有解析出，则与基本的路径计算出path
    const path = typeof parsedPath.protocol === 'string'
      || typeof parsedPath.host === 'string'
      || typeof parsedPath.port === 'string'
      ? parsedPath.path || ''
      : this.getPath(this.config.scheme?.path || '', parsedPath.path || '');

    // anchor 有值，覆盖
    const anchor = parsedPath.anchor ?? openOptions.anchor;

    const model = Object.assign({}, this.config.model, openOpts.model);

    const scheme = {
      protocol,
      host,
      anchor,
      params,
      path,
      port,
    };


    let intent: Intent | undefined;
    // 如果设置了intent
    if (this.config.intent) {
      const getIntent = () => {
        const { intent } = this.config;
        if (!intent) return;

        // 如果intent没有scheme参数，则使用config里的scheme参数
        if (!intent.scheme) {
          intent.scheme = this.config.scheme?.protocol;
        }

        // 如果传入的path中包含scheme的protocol, 则覆盖掉原来的
        if (parsedPath.protocol) {
          intent.scheme = protocol;
        }

        Object.assign(intent, openOptions.intent);
        return intent;
      };
      intent = getIntent();
    }

    return {
      model,
      scheme,
      intent,
    };
  }

  private async originalLaunchStrategy(context: OpenContext, launcher: Launcher): Promise<boolean> {
    const {
      fallbackUrl,
      intent,
      scheme,
      stringifyOptions,
    } = context;
    let isSuccess = false;
    const intentUrl = intent ? generateIntent(intent, scheme, stringifyOptions) : '';
    const schemeUrl = generateScheme(scheme, stringifyOptions);
    const urls: LaunchUrl = {
      intentUrl,
      schemeUrl,
    };

    if (ua.IS_IOS) {
      // 默认拉起策略
      isSuccess = await this.iosLaunch(urls, launcher);
    } else {
      // Android
      if (ua.IS_ORIGINAL_CHROME && intentUrl) {
        // 采用intent拉起
        isSuccess = await launcher.launchByLocation(intentUrl);
        // 不用跳转，intent会处理
        return isSuccess;
      }
      isSuccess = await this.androidLaunch(urls, launcher);
    }

    if (!isSuccess && fallbackUrl) {
      window.location.href = fallbackUrl;
    }

    return isSuccess;
  }

  /**
   * ios拉起策略
   */
  private async iosLaunch(context: LaunchUrl, launcher: Launcher): Promise<boolean> {
    const { schemeUrl } = context;

    // TODO: 支持universal link
    // const hasUniversal = universalUrl ?? true;
    const hasUniversal = false;
    let isSuccess = false;
    // ios qq 禁止 universalLink
    // ios qq 浏览器禁止了 universalLink
    // ios 微信 7.0.5 限制了universalLink
    // ios 微博禁止了 universalLink
    if ((ua.IS_WECHAT && ua.semverCompare(ua.getWeChatVersion(), '7.0.5') === -1) || ua.IS_WEIBO) {
      // 不用看了，拉不起的
      isSuccess = false;
    } else if (ua.getIOSVersion() < 9) {
      isSuccess = await launcher.launchByIframe(schemeUrl);
    } else if (!hasUniversal || ua.IS_QQ || ua.IS_QQ_BROWSER || ua.IS_QZONE) {
      isSuccess = await launcher.launchByLocation(schemeUrl);
    } else {
      // TODO: 使用universalLink拉起
      // launchByLocation(universalUrl);
      isSuccess = await launcher.launchByLocation(schemeUrl);
    }
    return isSuccess;
  }

  /**
   * android拉起策略
   */
  private async androidLaunch(context: LaunchUrl, launcher: Launcher): Promise<boolean> {
    let isSuccess = false;
    const { schemeUrl } = context;
    if (ua.IS_WECHAT || ua.IS_BAIDU || ua.IS_WEIBO || ua.IS_QZONE) {
      // 不用看了，也拉不起的
      isSuccess = false;
    } else {
      // 采用scheme拉起
      isSuccess = await launcher.launchByIframe(schemeUrl);
    }
    return isSuccess;
  }

  /**
   * 获取fallback的链接，并且判断是不是下载链接
   */
  private getFallbackUrl(
    fallback: AppLauncherConfig['fallback'],
    ctx: FnFallbackCtx,
  ): { isDownloadLink: boolean; fallbackUrl: string } {
    // 获取到链接
    let url = '';
    if (typeof fallback === 'function') {
      url = fallback(ctx);
    } else {
      url = fallback ?? '';
    }

    // 识别链接中的标识符
    const matches = url.match(/download:(.*)$/i);

    // 以 // 开头的地址在头部加上https:或者http:，以兼容intent的情况
    const relativeProtocol = (url: string): string => {
      let link = url;
      if (link.startsWith('//')) {
        link.startsWith('//');
        link = `${window.location.protocol}${link}`;
      }
      return link;
    };
    if (matches) {
      const fallbackUrl = relativeProtocol(matches[1] || '');
      return {
        isDownloadLink: true,
        fallbackUrl,
      };
    }
    const fallbackUrl = relativeProtocol(url);
    return {
      isDownloadLink: false,
      fallbackUrl,
    };
  }

  private afterLog(result: boolean): void {
    if (result) {
      this.logger.logKV('Launch Result', 'True (may success)');
    } else {
      this.logger.logKV('Launch Result', 'False (failed, pending or cancelled)');
    }
  }
}


